import { CatHeading } from 'blocks-nextjs/blocks-react/components/Heading';
import { CatTextLink } from 'blocks-nextjs/blocks-react/components/TextLink';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { LineItem } from 'types/parts-diagram';
import { TextLinkSize } from '@blocks-web-components/components/text-link/text-link.model';
import { capitalizeText } from '@cat-ecom/common/utils/commonUtils';
import { productPartsData } from 'types/product-diagram';
import { CartResponse } from 'services/cart/types';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { useEsiteCtx } from 'services/esite/context';
import {
  GA_PDP_DEFAULT_DIAGRAM,
  GA_PDP_LIST_DIAGRAM
} from 'constants/analyticsConstants';
import { parsePriceToFloat } from 'app/[lang]/[esite]/(core)/product/[product]/_components/anchor-menu/utils';

interface GetGroupPartTypeProps {
  partNumber: string;
  name: string;
  disambiguation: boolean;
  ieSystemControlNumber: string | null;
  price: ProductDealerPriceAvailability;
  handleOpen: (
    partNumber: string,
    price: string | ProductDealerPriceAvailability
  ) => void;
  lineItem?: LineItem;
  setDrawerActive: (drawerActive: boolean) => void;
  catHeadingClassName?: string;
  catLinkSize?: TextLinkSize;
  catTextCapitalize?: boolean;
  isActiveListTab?: boolean;
  fromProductPage?: boolean;
  cartDetails?: CartResponse;
  productData?: productPartsData;
}

const getLineItemIndex = (lineItem?: LineItem): number => {
  return lineItem?.referenceNumber ? parseInt(lineItem.referenceNumber) - 1 : 0;
};

const getItemListIdentifier = (isActiveListTab?: boolean): string => {
  return isActiveListTab ? GA_PDP_LIST_DIAGRAM : GA_PDP_DEFAULT_DIAGRAM;
};

const createSelectItemEventData = (props: {
  isActiveListTab?: boolean;
  name: string;
  storeId: string;
  lineItem?: LineItem;
  partNumber: string;
  price?: ProductDealerPriceAvailability;
  productData?: productPartsData;
  cartDetails?: CartResponse;
}) => {
  const {
    isActiveListTab,
    name,
    storeId,
    lineItem,
    partNumber,
    price,
    productData,
    cartDetails
  } = props;
  const index = getLineItemIndex(lineItem);
  const listIdentifier = getItemListIdentifier(isActiveListTab);
  const totalPrice = parsePriceToFloat(price?.totalPrice?.toString());

  return {
    itemListId: listIdentifier,
    itemListName: listIdentifier,
    partName: name || '',
    locationId: storeId || '',
    index,
    isSearchPageEvent: true,
    itemSummary: [
      {
        ...lineItem,
        index,
        imageURL: '',
        type: 'part',
        categoryPath: price?.categoryPath || productData?.categoryPath,
        manufacturer: productData?.manufacturer || 'cat',
        partNumber: partNumber
      }
    ],
    serialNumber: '',
    fitment: '',
    item: {
      orderItemId: cartDetails?.orderHeader?.orderId,
      partNumber: partNumber,
      quantity: lineItem?.quantity ? parseInt(lineItem.quantity) : 1,
      availabilityMessage: '',
      totalPrice: totalPrice
    }
  };
};

const GetGroupPartType = ({
  partNumber,
  name,
  disambiguation,
  ieSystemControlNumber,
  price,
  handleOpen,
  lineItem,
  setDrawerActive,
  catHeadingClassName = '',
  catLinkSize,
  catTextCapitalize,
  isActiveListTab,
  fromProductPage = false,
  cartDetails,
  productData
}: GetGroupPartTypeProps) => {
  const { setClickedLineItem } = usePartDiagram();
  const displayCapitalizeName = catTextCapitalize ? capitalizeText(name) : name;
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { storeId } = useEsiteCtx();

  const handleProductNameClick = () => {
    if (fromProductPage) {
      const eventData = createSelectItemEventData({
        isActiveListTab,
        name,
        storeId,
        lineItem,
        partNumber,
        price,
        productData,
        cartDetails
      });
      fireSelectItemEvent(eventData);
    }

    setDrawerActive(true);
    handleOpen(partNumber, price);
    if (lineItem) {
      setClickedLineItem(lineItem);
    }
  };

  const shouldShowTextLink = ieSystemControlNumber === null && !disambiguation;

  return shouldShowTextLink ? (
    <CatTextLink
      data-testid="normal-group-part"
      onClick={handleProductNameClick}
      size={catLinkSize}
    >
      {partNumber}
      {name && `: ${displayCapitalizeName}`}
    </CatTextLink>
  ) : (
    <CatHeading variant="body" className={catHeadingClassName}>
      {partNumber}
      {name && `: ${name}`}
    </CatHeading>
  );
};

export default GetGroupPartType;
